import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import { IGroupTutorRateVersion } from '../GroupTutorRateVersionsSlice';
import { IGroupTutorRateCourseType } from '../GroupTutorRateCourseTypesSlice';
import { IUser } from '../../UserSlice';
import { RootState } from '../..';
import {
  adminCreateGroupTutorRate,
  adminDeleteGroupTutorRate,
  adminGetGroupTutorRates,
  adminUpdateGroupTutorRate,
  getUserGroupTutorRatesAsAdmin,
} from './adminApi';
import { getGroupTutorRates } from './api';

export interface IGroupTutorRateAmount {
  _id: string;
  rateVersion: string | IGroupTutorRateVersion;
  amount: number;
  currency: string;
}

export interface IGroupTutorRate {
  _id: string;
  tutorCountry: string;
  courseType: string | IGroupTutorRateCourseType;
  amounts: IGroupTutorRateAmount[];
  minStudentCount: number;
  maxStudentCount: number;
  createdBy: string | IUser;
  updatedBy: string | IUser;
}

export interface IInitialGroupTutorRatesState {
  status: 'idle' | 'loading' | 'rejected';
  groupTutorRates: IGroupTutorRate[];
  // Admin
  adminGroupTutorRateListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IGroupTutorRate>;
  };
  adminUserGroupTutorRates: IGroupTutorRate[];
}

const initialState: IInitialGroupTutorRatesState = {
  status: 'idle',
  groupTutorRates: [],
  // Admin
  adminGroupTutorRateListData: undefined,
  adminUserGroupTutorRates: [],
};

export const GroupTutorRatesSlice = createSlice({
  initialState,
  name: 'groupTutorRates',
  reducers: {
    resetGroupTutorRates: (state) => {
      state.groupTutorRates = [];
    },
    // Admin
    resetAdminGroupTutorRateListData: (state) => {
      state.adminGroupTutorRateListData = undefined;
    },
    resetAdminUserGroupTutorRates: (state) => {
      state.adminUserGroupTutorRates = [];
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getGroupTutorRates.fulfilled, (state, action) => {
        const result = action.payload;

        state.groupTutorRates = result;
      })

      // Admin
      .addCase(adminGetGroupTutorRates.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminGroupTutorRateListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminGroupTutorRateListData = newAdminGroupTutorRateListData;
      })
      .addCase(adminCreateGroupTutorRate.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminGroupTutorRateListData) {
          const newAdminGroupTutorRateListData = {
            ...state.adminGroupTutorRateListData,
          };

          newAdminGroupTutorRateListData.list.unshift(result);

          if (newAdminGroupTutorRateListData.totalDocs) {
            newAdminGroupTutorRateListData.totalDocs += 1;
          } else {
            newAdminGroupTutorRateListData.totalDocs = 1;
          }

          state.adminGroupTutorRateListData = newAdminGroupTutorRateListData;
        }
      })
      .addCase(adminUpdateGroupTutorRate.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminGroupTutorRateListData &&
          state.adminGroupTutorRateListData.list &&
          state.adminGroupTutorRateListData.list.length
        ) {
          const newAdminGroupTutorRateListData = {
            ...state.adminGroupTutorRateListData,
          };

          const index = newAdminGroupTutorRateListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminGroupTutorRateListData.list[index] = result;
          }

          state.adminGroupTutorRateListData = newAdminGroupTutorRateListData;
        }
      })
      .addCase(adminDeleteGroupTutorRate.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminGroupTutorRateListData &&
          state.adminGroupTutorRateListData.list &&
          state.adminGroupTutorRateListData.list.length
        ) {
          const newAdminGroupTutorRateListData = {
            ...state.adminGroupTutorRateListData,
          };

          newAdminGroupTutorRateListData.totalDocs = newAdminGroupTutorRateListData.totalDocs
            ? newAdminGroupTutorRateListData.totalDocs - 1
            : 0;

          newAdminGroupTutorRateListData.list = newAdminGroupTutorRateListData.list.filter(
            (item) => item._id !== result._id
          );

          state.adminGroupTutorRateListData = newAdminGroupTutorRateListData;
        }
      })
      .addCase(getUserGroupTutorRatesAsAdmin.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminUserGroupTutorRates = result;
      });

    // Common Matchers
    builder.addMatcher(
      isFulfilled(
        getGroupTutorRates,
        // Admin
        adminGetGroupTutorRates,
        adminCreateGroupTutorRate,
        adminUpdateGroupTutorRate,
        adminDeleteGroupTutorRate,
        getUserGroupTutorRatesAsAdmin
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isPending(
        getGroupTutorRates,
        // Admin
        adminGetGroupTutorRates,
        adminCreateGroupTutorRate,
        adminUpdateGroupTutorRate,
        adminDeleteGroupTutorRate,
        getUserGroupTutorRatesAsAdmin
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        getGroupTutorRates,
        // Admin
        adminGetGroupTutorRates,
        adminCreateGroupTutorRate,
        adminUpdateGroupTutorRate,
        adminDeleteGroupTutorRate,
        getUserGroupTutorRatesAsAdmin
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getGroupTutorRatesState = (
  state: RootState
): RootState['groupTutorRates'] => state.groupTutorRates;

export const {
  resetGroupTutorRates,
  // Admin
  resetAdminGroupTutorRateListData,
  resetAdminUserGroupTutorRates,
} = GroupTutorRatesSlice.actions;

export * from './adminApi';
export default GroupTutorRatesSlice.reducer;
